import React from "react";
import firebase from "firebase/app";
import "firebase/database";
import Moment from "moment";
//@ts-ignore
import projectConfig from "@projectConfig";

import { v4 as uuidv4 } from "uuid";

import { checkMailReferences } from "../../../Modal/CreateChatModal/createChatModalScripts";
import { changeAssistantStatus, pushFirebase, updateNotificationList } from "./firebaseCRUD";
import { getAiResponse } from "../../../../services/restAPI/endpoints/ChatBotAPI";
import { ChatContextData } from "../../../../context/ChatContext/ChatContext";
import { ChatMessageInterface, ChatRoomInterface } from "./ChatInterfaces";
import { ChatSettingsState } from "../../../../appRedux/reduxInterfaces/ReducersInterfaces";

interface Item {
   key: string;
}

interface NewlineTextProps {
   text: string;
}

/**
 * Converts a Firebase DataSnapshot into an array of items. (firebase return data in snapshot. We need to convert it to work on)
 *
 * @param {firebase.database.DataSnapshot} snapshot - The Firebase snapshot to convert.
 * @returns {Item[]} - An array of items extracted from the snapshot.
 */
export const snapshotToArray = (snapshot: firebase.database.DataSnapshot): Item[] => {
   const returnArr: Item[] = [];
   snapshot.forEach((childSnapshot) => {
      const item: Item = { ...childSnapshot.val(), key: childSnapshot.key };
      returnArr.push(item);
   });
   return returnArr;
};

/**
 * A React functional component that splits text by newline characters and wraps each line in a <p> tag.
 *
 * @param {NewlineTextProps} props - The props for the component.
 * @param {string} props.text - The text to be split and displayed.
 * @returns {JSX.Element} - A JSX element containing the formatted text.
 */
export const NewlineText: React.FC<NewlineTextProps> = ({ text }) => {
   const newText = text.split("\n").map((str, index) => (
      <p key={index} className="gx-mb-0">
         {str}
      </p>
   ));
   return <>{newText}</>;
};

/**
 * Extracts the name from a full name string that does not match the provided first name.
 *
 * @param {string} fullName - The full name string, with names separated by " - ".
 * @param {string} firstName - The first name to exclude from the result.
 * @returns {string} - The other name found in the full name string, or an empty string if not found.
 */
export const getOtherName = (fullName: string, firstName: string): string => {
   const names: string[] = fullName.split(" - ");
   const otherName: string | undefined = names.find((name) => name !== firstName);
   return otherName || "";
};

/**
 * Checks if a room is closed based on chat data and optional mail data, case reference, and old ID.
 *
 * @param {any} chatData - The chat data to search through.
 * @param {object} [mailData] - Optional mail data to check.
 * @param {string} [caseReference] - Optional case reference to check.
 * @param {number} [oldId] - Optional old ID to check.
 * @returns {any} - Returns the found room if it exists, otherwise undefined.
 */
export const checkIfRoomIsClosed = async (
   chatData: any,
   mailData?: object,
   caseReference?: string,
   oldId?: number,
) => {
   if (mailData) {
      const foundRoom = chatData?.allRooms?.find(
         (room) => room.messageIdFromHeader === checkMailReferences(mailData),
      );
      if (foundRoom) return foundRoom;
   }

   if (caseReference && oldId) {
      const foundRoom = chatData?.allRooms?.find(
         (room) => room.caseReference === caseReference && room.owner === oldId,
      );
      if (foundRoom) return foundRoom;
   }

   if (caseReference) {
      const archivedRoom = chatData?.allRooms?.find(
         (room) =>
            room.caseReference === caseReference &&
            (room.status === "closed" || room.status === "archived"),
      );

      if (archivedRoom) {
         return archivedRoom;
      }

      // if can't find room in chatData check firebase directly
      try {
         const snapshot = await firebase.database().ref("rooms").once("value");
         const allRooms = snapshot.val();

         if (!allRooms) return undefined;

         const roomsArray = Object.entries(allRooms).map(([key, room]: [string, any]) => ({
            key,
            ...(room || {}),
         }));

         const firebaseArchivedRoom = roomsArray.find(
            (room) =>
               room.caseReference === caseReference &&
               (room.status === "closed" || room.status === "archived"),
         );

         if (firebaseArchivedRoom) {
            return firebaseArchivedRoom;
         }
      } catch (error) {
         console.error("Error searching Firebase for archived rooms:", error);
      }

      // if no archived room found found get any active room
      const foundRoom = chatData?.allRooms?.find((room) => room.caseReference === caseReference);
      if (foundRoom) return foundRoom;
   }

   return;
};
/**
 * Checks if a mail room exists in the chat data.
 *
 * Filter out rooms with "archived" status
 *
 * @param {any} chatData - The chat data to search through.
 * @param {object} data - The data to use for checking mail references.
 * @returns {boolean} - Returns true if the mail room exists, otherwise false.
 */
export const checkIfMailRoomExist = (chatData: any, data: object) => {
   return (
      filterInactiveRooms(chatData?.allRooms)?.some(
         (room) => room.messageIdFromHeader === checkMailReferences(data),
      ) ?? false
   );
};

/**
 * Checks if a case room exists in the chat data.
 *
 *  Filter out rooms with "archived" status
 *
 * @param {any} chatData - The chat data to search through.
 * @param {string} caseReference - The case reference to check for.
 * @returns {boolean} - Returns true if the case room exists, otherwise false.
 */
export const checkIfCaseRoomExist = (chatData: any, caseReference: string) => {
   return (
      filterInactiveRooms(chatData?.allRooms)?.some(
         (room) => room.caseReference === caseReference,
      ) ?? false
   );
};

/**
 * Checks if the selected room is a direct room.
 *
 * @param {any} chatData - The chat data to search through.
 * @param {any} chatSettings - The chat settings to use for checking the selected room.
 * @returns {boolean} - Returns true if the selected room is a direct room, otherwise false.
 */
export const checkIsSelectedRoomDirect = (chatData: any, chatSettings: any) => {
   return chatData?.directRooms?.some((room) => room.id === chatSettings?.selectedRoomId) ?? false;
};

/**
 * Handles the submission of a message.
 *
 * @param {React.FormEvent} e - The form submission event.
 * @param {any} newMessage - The new message object to be submitted.
 * @param {string} oldId - The ID of the user sending the message.
 * @param {any} chatSettings - The settings of the chat.
 * @param {string} firstName - The first name of the user sending the message.
 * @param {string} lastName - The last name of the user sending the message.
 * @param {function} setNewMessage - Function to reset the new message state.
 * @param {function} updateNotificationList - Function to update the notification list.
 * @param {any} chatData - The chat data containing rooms and users.
 * @param {function} setShowErrorMessage - Function to set the error message state.
 */
export const submitMessage = (
   e,
   newMessage,
   oldId,
   chatSettings,
   firstName,
   lastName,
   setNewMessage,
   updateNotificationList,
   chatData,
   setShowErrorMessage,
   intl,
) => {
   e.preventDefault();

   const currentRoom = chatData?.allRooms.find(
      (room) => room?.id === chatSettings?.currentRoom?.id,
   );
   const newUUID = uuidv4();

   // Check if the message intended for the assistant is empty or whitespace
   if (
      isMessageForAiBot(newMessage?.content) &&
      isEmptyOrWhitespace(removeBotNameFromMessage(newMessage.content)) &&
      currentRoom?.hasOwnProperty("GPTBotId") &&
      currentRoom?.hasOwnProperty("GPTThreadId")
   ) {
      setShowErrorMessage({
         showErrorMessage: true,
         errorMessage: intl.formatMessage({ id: "chat.provide.message.for.assistant!" }),
      });
      setTimeout(() => {
         setShowErrorMessage({
            showErrorMessage: false,
            errorMessage: "",
         });
      }, 5000);
      return;
   }

   const chat = {
      ...newMessage,
      userId: oldId,
      title: chatSettings?.currentRoom?.title,
      roomId: chatSettings?.currentRoom?.id,
      chatUser: `${firstName} ${lastName}`,
      date: Moment(new Date()).format("DD/MM/YYYY HH:mm:ss"),
      type: "message",
      messageId: newUUID,
   };

   // firebase api for pushing messages
   const pushMessage = pushFirebase("messages/");

   if (isMessageForAiBot(newMessage?.content)) {
      // If the message is intended for the AI bot, handle the AI response
      handleAiMessage(
         chatSettings,
         chatData,
         newMessage,
         updateNotificationList,
         oldId,
         setShowErrorMessage,
         intl,
      );
   }

   // Send user message and handle response based on the assistant's busy status
   if (
      (isMessageForAiBot(newMessage?.content) && currentRoom?.isAsistantBusy !== "true") ||
      !isMessageForAiBot(newMessage?.content)
   ) {
      // If the message is for the AI bot and the assistant is not busy, or if the message is not for the AI bot:
      // 1. Send the message
      // 2. Clear the newMessage state
      // 3. Update the notification list
      pushMessage.set(chat);
      setNewMessage({
         userId: "",
         roomId: "",
         title: "",
         chatUser: "",
         content: "",
         date: "",
         type: "",
      });
      updateNotificationList("rooms/", chatSettings?.currentRoom?.key, [oldId]);
   } else {
      // If the message is for the AI bot and the assistant is busy:
      // 1. Show an error message indicating the assistant is busy
      // 2. Reset the error message after 5 seconds
      setShowErrorMessage({
         showErrorMessage: true,
         errorMessage: intl.formatMessage({ id: "chat.busy.assistant" }),
      });
      setTimeout(() => {
         setShowErrorMessage({
            showErrorMessage: false,
            errorMessage: "",
         });
      }, 5000);
   }
};
/**
 * Handles messages directed to the AI bot.
 *
 * @param {any} chatSettings - The chat settings to use for the message.
 * @param {any} chatData - The chat data containing rooms and users.
 * @param {any} messageData - The message data to handle.
 * @param {function} setNewMessage - Function to reset the new message state.
 * @param {function} updateNotificationList - Function to update the notification list.
 * @param {string} userOldId - The ID of the user sending the message.
 * @param {function} setShowErrorMessage - Function to set the error message state.
 */
const handleAiMessage = (
   chatSettings,
   chatData,
   messageData,
   updateNotificationList,
   userOldId,
   setShowErrorMessage,
   intl,
) => {
   const currentRoom = chatData?.allRooms.find(
      (room) => room?.id === chatSettings?.currentRoom?.id,
   );
   const currentBot = chatData?.users?.find((user) => user.oldId === currentRoom?.GPTBotId);

   // Check if the bot is present in the room, the bot thread exists, and the assistant is not busy
   if (
      (currentRoom?.GPTBotId &&
         currentRoom?.GPTThreadId &&
         currentRoom?.isAsistantBusy === "false") ||
      (currentRoom?.GPTBotId &&
         currentRoom?.GPTThreadId &&
         !currentRoom?.hasOwnProperty("isAsistantBusy"))
   ) {
      // Change the assistant's status to "true" to indicate it is busy processing the message
      changeAssistantStatus("rooms/", currentRoom.key, "true");

      // Get the response from the AI bot
      getAiResponse(
         currentRoom.GPTBotId,
         currentRoom.mailId,
         currentRoom.GPTThreadId,
         removeBotNameFromMessage(messageData.content),
      )
         .then(
            (res) =>
               (
                  // Send the message as the bot
                  sendMessageAsBot(
                     chatSettings,
                     currentBot,
                     res.data,
                     updateNotificationList,
                     userOldId,
                  ),
                  // Change the assistant's status to "false" to indicate it is free
                  changeAssistantStatus("rooms/", currentRoom.key, "false")
               ),
         )
         .catch(
            (error) =>
               (
                  // In case of an error, display the error message
                  console.error(error),
                  setShowErrorMessage({
                     showErrorMessage: true,
                     errorMessage: intl.formatMessage({ id: "chat.assistant.have.problem" }),
                  }),
                  // After 5 seconds, reset the error message and change the assistant's status to "false" to indicate it is free
                  setTimeout(() => {
                     changeAssistantStatus("rooms/", currentRoom.key, "false");
                     setShowErrorMessage({
                        showErrorMessage: false,
                        errorMessage: "",
                     });
                  }, 5000)
               ),
         );
   }
};

/**
 * Determines if the user input is directed to the AI bot.
 *
 * @param {string} userInput - The user's input message.
 * @returns {boolean} - Returns true if the input is directed to the AI bot, otherwise false.
 */
const isMessageForAiBot = (userInput) => {
   const regex = /^@\S+/;
   return regex.test(userInput);
};

/**
 * Removes the bot name from the user's message.
 *
 * @param {string} str - The user's message.
 * @returns {string} - The message without the bot name.
 */
const removeBotNameFromMessage = (str) => {
   const regex = /^@\S+\s*/;
   return str.replace(regex, "");
};

/**
 * Sends a message as the bot.
 *
 * @param {any} chatSettings - The chat settings to use for the message.
 * @param {any} currentBot - The bot user data.
 * @param {string} message - The message content.
 * @param {function} setNewMessage - Function to reset the new message state.
 * @param {function} updateNotificationList - Function to update the notification list.
 * @param {string} userOldId - The ID of the user sending the message.
 */
const sendMessageAsBot = (chatSettings, currentBot, message, updateNotificationList, userOldId) => {
   const pushMessage = pushFirebase("messages/");
   const newUUID = uuidv4();
   const botMessage = {
      userId: currentBot?.oldId,
      title: chatSettings?.currentRoom?.title,
      roomId: chatSettings?.currentRoom?.id,
      chatUser: `${currentBot?.firstName} ${currentBot?.lastName}`,
      date: Moment(new Date()).format("DD/MM/YYYY HH:mm:ss"),
      type: "botMessage",
      messageId: newUUID,
      content: JSON.stringify(message),
   };

   pushMessage.set(botMessage);
   updateNotificationList("rooms/", chatSettings?.currentRoom?.key, [userOldId]);
};

/**
 * Checks if a given string is empty or consists only of whitespace characters.
 *
 * @param {string} str - The string to check.
 * @returns {boolean} - Returns true if the string is empty or contains only whitespace, false otherwise.
 */
const isEmptyOrWhitespace = (str) => {
   return !str.trim();
};

/**
 * Checks if the assistant should be unlocked based on the latest bot message's timestamp.
 * The assistant should be unlocked if the latest bot message is older than 2 minutes from the current time.
 *
 * @param {any[]} messages - An array of message objects. Each message object is expected to have a `date` property and a `type` property.
 * @returns {boolean} - Returns `true` if the latest bot message is older than 2 minutes from the current time, otherwise `false`.
 *
 */
export const checkIfAsystentShouldBeUnlocked = (messages: any[]): boolean => {
   const botMessages = messages?.filter((message) => message.type === "botMessage");

   if (!botMessages || botMessages.length === 0) {
      return false;
   }

   const latestBotMessage = findLatestMessage(botMessages);

   const formatDate = (date) => {
      const d = new Date(date);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      const hours = String(d.getHours()).padStart(2, "0");
      const minutes = String(d.getMinutes()).padStart(2, "0");
      const seconds = String(d.getSeconds()).padStart(2, "0");

      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
   };

   if (checkIfMoreThanTwoMinutes(latestBotMessage?.date, formatDate(new Date()))) {
      return true;
   } else return false;
};

const checkIfMoreThanTwoMinutes = (lastDate: string, currentDate: string): boolean => {
   const startDate = new Date(lastDate);
   const endDate = new Date(currentDate);

   if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      console.error("Invalid date format");
      return false;
   }

   const diffInMilliseconds: number = endDate.getTime() - startDate.getTime();

   const diffInMinutes: number = diffInMilliseconds / (1000 * 60);

   return diffInMinutes > 2;
};

const findLatestMessage = (messages: any[]) => {
   return messages.reduce((latestMessage, currentMessage) => {
      const latestDate = new Date(latestMessage.date).getTime();
      const currentDate = new Date(currentMessage.date).getTime();

      return currentDate > latestDate ? currentMessage : latestMessage;
   });
};

/**
 * Returns the modal size (width or height) based on the specified dimension and fullscreen mode.
 *
 * @param {"width" | "height"} dimension - The dimension to retrieve, either "width" or "height".
 * @param {boolean} fullScreenMode - A flag indicating whether the modal is in fullscreen mode.
 * @returns {number} The size of the specified dimension in pixels.
 *
 * default value for fullscreen is user viewport / 1.01, and for non fullscren viewport / 1.5
 *
 * @example
 * // Get the width of a modal in fullscreen mode
 * const width = getModalSize("width", true); // Returns `window.innerWidth`
 *
 */
export const getChatModalSize = (dimension: "width" | "height", fullScreenMode) => {
   if (fullScreenMode === true) {
      if (dimension === "width") {
         return window?.innerWidth / 1.01;
      } else if (dimension === "height") {
         return window?.innerHeight / 1.01;
      }
   } else {
      if (dimension === "width") {
         return window?.innerWidth / 1.5;
      } else if (dimension === "height") {
         return window?.innerHeight / 1.5;
      }
   }
};

/**
 * Finds all chat rooms associated with a specific user ID.
 *
 * @param {ChatContextData} chatData - The chat data context containing information about all rooms and users.
 * @param {number} oldId - The unique identifier of the user to find chat rooms for.
 * @returns {Array<ChatRoom> | undefined} An array of chat rooms the user is a part of, or `undefined` if no rooms are found.
 *
 *
 */
export const findMyChatRooms = (chatData: ChatContextData, oldId: number) => {
   let myRooms = chatData?.chatData?.allRooms?.filter((room) => room?.chatUsers?.includes(oldId));

   return myRooms;
};

/**
 * Sorts the rooms by the date of their latest message.
 *
 * This function takes in a list of rooms and messages, parses the message dates, and sorts the rooms in descending order based on the most recent message in each room.
 * Rooms with no messages will appear at the end of the list.
 *
 * @param {ChatRoomInterface[]} rooms - The list of chat rooms to be sorted.
 * @param {ChatMessageInterface[]} messages - The list of messages used to determine the latest message for each room.
 *
 * @returns {ChatRoomInterface[]} A new array of rooms sorted by the latest message date, with the most recent first.
 */
export const sortRoomsByLatestMessage = (
   rooms: ChatRoomInterface[],
   messages: ChatMessageInterface[],
): ChatRoomInterface[] => {
   const parseDate = (dateString: string): Date => {
      if (!dateString) {
         return new Date(0);
      }

      const [day, month, yearTime] = dateString.split("/");

      if (!yearTime || !day || !month) {
         return new Date(0);
      }

      const [year, time] = yearTime.split(" ");
      if (!time || !year) {
         return new Date(0);
      }

      return new Date(`${year}-${month}-${day}T${time}`);
   };

   const roomLastMessageMap = messages.reduce<Record<string, Date>>((acc, message) => {
      const messageDate = parseDate(message.date);
      if (!acc[message.roomId] || acc[message.roomId] < messageDate) {
         acc[message.roomId] = messageDate;
      }
      return acc;
   }, {});

   return rooms.sort((a, b) => {
      const dateA = roomLastMessageMap[a.id] || new Date(0);
      const dateB = roomLastMessageMap[b.id] || new Date(0);
      return dateB.getTime() - dateA.getTime();
   });
};

/**
 * Function to split the date into the date part (day/month/year) and replace "/" with ".".
 *
 * @param {string} dateTime - The date in the format "DD/MM/YYYY HH:MM:SS".
 * @returns {string} - Returns the date part in the format "DD.MM.YYYY".
 */
export const splitDate = (dateTime: string): string => {
   if (dateTime) {
      const datePart = dateTime.split(" ")[0]; // Extract only the date part (DD/MM/YYYY)
      return datePart.replace(/\//g, "."); // Replace "/" with "."
   }
   return ""; // If there is no date, return an empty string
};

/**
 * Finds a user in the chat data by their unique ID.
 *
 * @param {number | string} userId - The unique identifier of the user to find.
 * @param {ChatContextData} chatData - The chat data containing user information.
 * @returns {object | null} - Returns the user object if found, otherwise returns `null`.
 *
 * @example
 * // Example usage
 * const user = findUserById(10586, chatData);
 * if (user) {
 *   console.log(`Found user: ${user.name}`);
 * } else {
 *   console.log('User not found');
 * }
 */
export const findUserById = (userId: number | string, chatData: ChatContextData) => {
   if (chatData && userId) {
      const foundUser = chatData?.chatData?.filteredPhpUsers?.find(
         (user) => user?.oldId === userId,
      );
      return foundUser;
   } else {
      return null;
   }
};

/**
 * Finds the avatar URL for a user based on the user's ID from a list of avatars.
 *
 * This function looks for an avatar object in the provided `avatarsList` that matches
 * the given `userId`. If the avatar is found, it returns the URL of the avatar image.
 * If no matching avatar is found, or if the avatar doesn't contain a URL, it returns `null`.
 *
 * @param {number | string} userId - The ID of the user whose avatar is to be found.
 * @param {Array} avatarsList - The list of avatar objects to search in. Each avatar should have a `userOldId` and `avatarUrl`.
 * @returns {string | null} The avatar URL for the user if found, otherwise `null`.
 */
export const findUserAvatarByUserId = (userId: number | string, avatarsList: any) => {
   const userAvatar = avatarsList?.find((avatar) => avatar?.userOldId === userId);
   if (userAvatar?.avatarUrl) {
      return userAvatar?.avatarUrl;
   } else {
      return null;
   }
};

/**
 * Finds the current chat room based on the provided `chatData` and `chatSettings`.
 *
 * This function searches through all available rooms in `chatData` and returns the
 * room that matches the ID of the current room in `chatSettings`. If no matching room is found,
 * it returns `null`.
 *
 * @param {ChatContextData} chatData - The context data containing all available chat rooms.
 * @param {ChatSettingsState} chatSettings - The settings object containing the current room's ID.
 * @returns {Room | null} The room that matches the current room's ID from `chatSettings`, or `null` if no match is found.
 */
export const findCurrentChatRoom = (chatData: ChatContextData, chatSettings: ChatSettingsState) => {
   if (chatData && chatSettings) {
      return chatData?.chatData?.allRooms?.find(
         (room) => room.id === chatSettings?.currentRoom?.id,
      );
   } else return null;
};

/**
 * Filters out inactive rooms from the provided list of rooms.
 *
 * @param {Array<Object>} rooms - An array of room objects to filter.
 * Each room object is expected to have a `status` property.
 *
 * @returns {Array<Object>} - A new array containing only the active rooms
 * (rooms where `status` equals `"active"`). If the input array is empty or undefined, returns an empty array.
 *
 */
export const filterInactiveRooms = (rooms) => {
   if (rooms?.length < 1) return [];

   const activeRooms = rooms?.filter((room) => room?.status === "active");
   return activeRooms;
};

/**
 * Marks messages in a chat room as "seen" by adding the user's ID to the `seenBy` list.
 *
 * @param {Object} room - The chat room object where the messages are being marked as seen.
 * @param {string} oldId - The unique identifier of the user who has seen the messages.
 *
 * @returns {void} - This function does not return a value.
 */
export const markMessagesAsSeen = (room, oldId) => {
   if (room?.seenBy?.includes(oldId)) return;

   const newSeenByList = [...room?.seenBy, oldId];

   updateNotificationList("rooms/", room?.key, newSeenByList);
};

/**
 * Filters a list of objects to include only those that:
 * - Have non-empty, valid fields: firstName, lastName, and oldId.
 * - Are not undefined, null, NaN, or empty strings for those fields.
 *
 * @param {Array} users - The list of user objects to filter.
 * @returns {Array} The filtered list of users.
 */
export const filterChatUsers = (users) => {
   if (!Array.isArray(users)) {
      console.error("Invalid input: Expected an array.");
      return [];
   }

   const filteredPhpUsers = users.filter(
      (user) =>
         user?.firstName &&
         user?.lastName &&
         user?.oldId &&
         user.firstName !== "" &&
         user.lastName !== "" &&
         user.oldId !== "",
   );

   return filteredPhpUsers;
};

/**
 * Finds an existing chat room (direct) between the current user and a target user.
 *
 * @param {Array} rooms - The list of available chat rooms.
 * @param {number} currentUserId - The ID of the current user.
 * @param {number} targetUserId - The ID of the user to find a chat room with.
 * @returns {Object|undefined} The existing room object if found, otherwise undefined.
 */
export const findExistingRoom = (rooms, currentUserId, targetUserId) => {
   return rooms?.find(
      (room) =>
         (room.chatUsers?.[0] === currentUserId && room.chatUsers?.[1] === targetUserId) ||
         (room.chatUsers?.[0] === targetUserId && room.chatUsers?.[1] === currentUserId),
   );
};

/**
 * Creates a new chat room with specified users and type.
 *
 * @param {string} roomId - The unique identifier for the new chat room.
 * @param {"direct" | "private"} [type="direct"] - The type of the chat room, either "direct" or "private".
 * @param {number} [currentUserId] - The ID of the current user (user oldId).
 *
 *
 * @param {string} [roomTitle] - The title of the chat room (For private rooms).
 * @param {Array<number>} [chatUsers] - An array of user IDs participating in the chat (For private rooms).
 *
 * @param {number} [targetUserId] - The ID of the target user (For direct rooms).
 * @param {string} [currentUserName] - The name of the current user (For direct rooms).
 * @param {string} [targetUserName] - The name of the target user (For direct rooms).
 * @returns {Object} The newly created room object.
 */
export const createNewRoom = (
   roomId,
   type: "direct" | "private" = "direct",
   roomTitle?,
   chatUsers?,
   currentUserId?,
   currentUserName?,
   targetUserName?,
) => {
   const newRoomRef = firebase.database().ref("rooms").push();

   const key = newRoomRef.key;

   const roomData = {
      id: roomId,
      title: roomTitle || generateRoomTitle(currentUserName, targetUserName),
      type,
      status: "active",
      seenBy: [currentUserId],
      chatUsers: chatUsers,
      chatMessage: [],
   };

   newRoomRef.set(roomData);

   return { key: key, ...roomData };
};

/**
 * Generates a title for a chat room based on the users' first names.
 *
 * @param {string} userFirstName - The first name of the current user.
 * @param {string} targetUserFirstName - The first name of the target user.
 * @returns {string} The generated room title.
 */
export const generateRoomTitle = (userFirstName, targetUserFirstName) => {
   return userFirstName
      ? `${userFirstName} - ${targetUserFirstName || "Set Room Title"}`
      : "Set Room Title";
};

/**
 * Removes a given name or title from a room title.
 *
 * @param {string} roomTitle - The full room title (e.g., "Miłosz - Mateusz").
 * @param {string} nameToRemove - The name or title to remove.
 * @returns {string} The updated room title with the name removed.
 */
export const removeNameFromTitle = (roomTitle, nameToRemove) => {
   if (!roomTitle || !nameToRemove) return roomTitle;

   const updatedTitle = roomTitle
      .replace(new RegExp(`^${nameToRemove} - | - ${nameToRemove}$|^${nameToRemove}$`), "")
      .trim();

   return updatedTitle || roomTitle;
};

/**
 * Parses the bot message content and extracts relevant fields.
 *
 * @param {string} messageContent - The raw message content, which may be a JSON string.
 * @returns {{ responseForUser: string, suggestedEmailText: string, assistantUtils?: any }}
 *          An object containing:
 *          - `responseForUser`: The message intended for the user.
 *          - `suggestedEmailText`: Suggested email content (if available).
 *          - `assistantUtils`: Additional assistant utilities (optional).
 *
 * @example
 * const message = '{"assistantMessage": {"response_for_user": "Hello!", "suggested_email_text": "Hi, how can I help?"}}';
 * const parsed = parseBotMessage(message);
 * console.log(parsed.responseForUser); // "Hello!"
 */
export const parseBotMessage = (messageContent) => {
   if (!messageContent || messageContent.trim() === "") {
      return { responseForUser: messageContent, suggestedEmailText: messageContent };
   }

   let parsedContent;
   try {
      parsedContent = JSON.parse(messageContent);

      if (typeof parsedContent.assistantMessage === "string") {
         return {
            responseForUser: parsedContent.assistantMessage,
            suggestedEmailText: "",
         };
      }

      return {
         responseForUser: parsedContent?.assistantMessage?.response_for_user
            ? parsedContent?.assistantMessage?.response_for_user
            : parsedContent?.responseForUser || "",
         suggestedEmailText: parsedContent?.assistantMessage?.suggested_email_text
            ? parsedContent?.assistantMessage?.suggested_email_text
            : parsedContent?.suggestedEmailText || "",
         assistantUtils: parsedContent?.assistantUtils,
      };
   } catch (error) {
      console.error("Failed to parse message content:", error);
      return { responseForUser: messageContent, suggestedEmailText: "" };
   }
};

/**
 * removes duplicates from users list to chat
 */
export const removeDuplicates = (list) => {
   const seen = new Set();
   return list.filter((item) => {
      const uniqueKey = item.oldId;
      if (seen.has(uniqueKey)) {
         return false;
      }
      seen.add(uniqueKey);
      return true;
   });
};
